import React, { useEffect, useState, useContext } from "react";
import { Alert, Button, Col, Container, Form, Modal, Row, Table, InputGroup } from "react-bootstrap";
import { NavigationContext } from "../../Context/NavigationContext";
import { NavObjectType, } from "../navigationTypes";
import { ClioService } from "./ClioService";
import { Matter } from "./types";
import Loading from "../Loading";

export type ClioModalProps = {
    showModal: boolean;
    onHideModal: () => void;
}

const ClioIntegration = (props: ClioModalProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isTokenValid, setIsTokenValid] = useState<boolean>(false);
    const [isMatterModalOpen, setIsMatterModalOpen] = useState<boolean>(false);
    const [matters, setMatters] = useState<Matter[]>([]);
    const [matterSelected, setMatterSelected] = useState<Matter | null>(null);
    const [searchItem, setSearchItem] = useState<string>('');

    const nav = useContext(NavigationContext);

    window["deauthorizeAllTokens"] = ClioService.deauthorizeAllTokens;

    useEffect(() => {
        const checkTokenValid = async () => {
            setIsLoading(true);
            const tokenInfo = await ClioService.getTokenInfo(false, true);
            setIsTokenValid(tokenInfo !== null);
            setIsLoading(false);
        }
        checkTokenValid();
    }, []);

    useEffect(() => {
        const fetchMatters = async () => {
            setIsLoading(true);
            await getMatters();
            setIsLoading(false);
        }
        if (isMatterModalOpen)
            fetchMatters();
    }, [isMatterModalOpen]);

    const filteredMatters = matters.filter((matter: Matter) => {
        return (
            matter.display_name?.toLowerCase().includes(searchItem.toLowerCase()) ||
            (matter.client.first_name + " " + matter.client.last_name).toLowerCase().includes(searchItem.toLowerCase()) ||
            matter.responsible_attorney?.name.toLowerCase().includes(searchItem.toLowerCase())
        );
    });

    async function loginClio() : Promise<boolean>{
        const AUTH_CODE = await ClioService.getAuthCode();
    
        let tokenInfo;
        if (AUTH_CODE !== null && AUTH_CODE !== "") {
            tokenInfo = await ClioService.getTokenFromAuthCode(AUTH_CODE);
            if (tokenInfo !== null) {
                return true;
            }
        } else {
            console.log("No auth code found.");
            return false;
        }
    
        return false;
    }
    
    async function getMatters(): Promise<void> {
        setIsLoading(true);
        let fileType  = "PSM";  // get matters for all PSM types
        let matters = await ClioService.getPSMMatters(fileType);
        if (matters) {
            setMatters(matters);
        }
        setIsLoading(false);
    }

    function handleMatterClicked(matter: Matter) {
        setMatterSelected(matter);
        setIsMatterModalOpen(false);
    }

    async function handleImportMatter(nav: (frame: NavObjectType) => void, matterSelected: Matter) {
        let matterName = matterSelected.display_name;
        if (matterName === null || matterName === "")
            throw new Error("Name of selected Matter is empty");

        let matter = await ClioService.getMatterByName(matterName);

        if (matter) {
            let lawyer = null;
            if (matter.responsible_attorney != null)
                lawyer= await ClioService.getRPWLawyer(matter.responsible_attorney)
            matter.responsible_attorney = lawyer;

            await ClioService.postClioMatterToRPW(nav, matter);
        } else {
            throw new Error("Selected Matter not found in Clio");
        }
    }

    return (
        <>
            <Modal
                show={props.showModal}
                onHide={props.onHideModal}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                size="lg"
            >
                {isLoading ? <Loading /> : null}

                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Create File from Clio Matter</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {!isTokenValid ? (
                            <Container>
                                <Alert>
                                    To import a file from Clio Manage, please allow account access. This process will open in a new window.
                                </Alert>
                                <Button variant="primary" onClick={async () => {
                                    var isLoggedIn = false;
                                    try {
                                        setIsLoading(true);
                                        isLoggedIn = await loginClio();
                                    } catch (error) {
                                        console.error("Import Error:", error);
                                    } finally {
                                        setIsTokenValid(isLoggedIn);
                                        setIsLoading(false);
                                    }
                                }}>Access Clio Manage</Button>
                            </Container>
                    ) : (
                        <Container>
                            <Row>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Realti Information</th>
                                            <th>Clio Information</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>File Type</td>
                                            <td>Practice Area</td>
                                        </tr>
                                        <tr>
                                            <td>Responsible Lawyer</td>
                                            <td>Responsible Attorney</td>
                                        </tr>
                                        <tr>
                                            <td>Closing Date</td>
                                            <td>Closed Date</td>
                                        </tr>
                                        <tr>
                                            <td>Client Details</td>
                                            <td>Client Details</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Group>
                                        <Form.Label>Matter Name</Form.Label>
                                        <Form.Control
                                            name='matterName'
                                            readOnly
                                            value={matterSelected ? matterSelected.display_name : "Not selected"}
                                        />
                                        <br/>
                                        <Button variant="primary" onClick={async () => {
                                                setIsLoading(true);
                                                setIsMatterModalOpen(true);
                                                setIsLoading(false);
                                            }
                                        }>Find Clio Matter</Button>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Container>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={props.onHideModal}>Cancel</Button>
                    {matterSelected && !isMatterModalOpen ? (
                        <Button variant="primary" onClick={async () => {
                            try {
                                setIsLoading(true);
                                await handleImportMatter(nav, matterSelected);
                            } catch (error) {
                                console.error("Import Error:", error);
                            } finally {
                                setIsLoading(false);
                            }
                        }
                        }>Import</Button>
                    ) : null}
                </Modal.Footer>
            </Modal>

            <Modal
                show={isMatterModalOpen}
                backdrop="static"
                keyboard={false}
                size="lg"
                className="modal-list"
                centered
            >
                <Modal.Header>
                    <InputGroup className="search-bar">
                        <InputGroup.Text>
                            <i style={{ cursor: "default" }} className="fa-light fa-magnifying-glass fa-indicator"></i>
                        </InputGroup.Text>

                        <Form.Control
                            placeholder="Search for Matter"
                            value={searchItem}
                            onChange={(e) => {
                                setSearchItem(e.target.value)
                            }}>
                        </Form.Control>
                    </InputGroup>
                </Modal.Header>

                <Modal.Body>
                    <Table striped hover >
                        <thead>
                            <tr>
                                <th>Matter Name</th>
                                <th>Client Name</th>
                                <th>Responsible Lawyer</th>
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={3} style={{ textAlign: "center" }}>
                                        <b>Loading...</b>
                                    </td>
                                </tr>
                            ) : (
                                filteredMatters.map((matter, index) => {
                                    return (
                                        <React.Fragment key={matter.id + '-matter-' + index}>
                                            <tr onClick={() => {
                                                setSearchItem('');
                                                handleMatterClicked(matter);
                                            }}>
                                                <td>{matter.display_name?? "N/A"}</td>
                                                <td>{(matter.client.name)?? "N/A"}</td>
                                                <td>{matter.responsible_attorney ? matter.responsible_attorney.name : "N/A"}</td>
                                            </tr>
                                        </React.Fragment>
                                    )
                                })
                            )}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setSearchItem('');
                        setMatterSelected(null);
                        setIsMatterModalOpen(false);
                        }
                    }>Cancel</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default ClioIntegration;